import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Laptop, Landmark, BookOpen, Sun, Sunset, Moon, MapPin, Wallet as WalletIcon, Star, Palette, Stethoscope, ArrowUpDown, Lock, X } from 'lucide-react';
import type { AcademyAdvies, AcademyLeervorm, AcademyShift, AcademySortering, Wallet } from '../../services/medewerkerApi';
import { KM_MAX, KM_MIN, KM_STAP, SCORES, bewaarPostcode, normaliseerPostcode4, reistijdActief, wkrGrendelVast } from './academyFilters';
import type { AcademyFilters, ScoreDrempel } from './academyFilters';

const VORM_ICON: Record<AcademyLeervorm, React.ElementType> = { e_learning: Laptop, workshop: Landmark, opleiding: BookOpen };
const SHIFT_ICON: Record<AcademyShift, React.ElementType> = { dag: Sun, avond: Sunset, nacht: Moon };
const DAGEN = [1, 2, 3, 4, 5, 6, 7] as const;

const knop = (actief: boolean, extra = '') =>
  `inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-40 ${
    actief ? 'border-[#0B1F3A] bg-[#0B1F3A] text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
  } ${extra}`;

const selectCls = 'w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-900 focus:border-[#0B1F3A] focus:outline-none';

/** Groep in de linker filter-sidebar: klein kopje + inhoud. */
export const FilterGroep: React.FC<{ titel: string; children: React.ReactNode }> = ({ titel, children }) => (
  <div role="group" aria-label={titel}>
    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">{titel}</p>
    {children}
  </div>
);

function wissel<T>(lijst: T[], item: T): T[] {
  return lijst.includes(item) ? lijst.filter((x) => x !== item) : [...lijst, item];
}

/**
 * De consumentenfilters (harde criteria), de slimme/fiscale triggers en de sorteer-engine.
 * Leervorm, shift, dagen, budget, score, WKR en advies gaan naar de server; de
 * Reistijd-slider blijft 100% lokaal (postcode in localStorage).
 * Verticaal paneel voor de linker sidebar: zoekveld (slot) → sorteren → thema's (children) → filters.
 */
export const AcademyFilterBalk: React.FC<{
  filters: AcademyFilters;
  onChange: (f: AcademyFilters) => void;
  wallet: Wallet | undefined;
  advies: AcademyAdvies | null;
  postcode: string | null;
  onPostcode: (pc: string | null) => void;
  /** Zoekveld bovenaan de sidebar. */
  zoekveld?: React.ReactNode;
  /** Extra groepen direct na het sorteren (bijv. hoofdgroepen en thema's). */
  children?: React.ReactNode;
}> = ({ filters, onChange, wallet, advies, postcode, onPostcode, zoekveld, children }) => {
  const { t } = useTranslation('medewerker');
  const wkrVast = wkrGrendelVast(wallet);
  const reistijd = reistijdActief(filters);
  const [pcInvoer, setPcInvoer] = useState(postcode ?? '');
  const [pcFout, setPcFout] = useState(false);

  useEffect(() => setPcInvoer(postcode ?? ''), [postcode]);

  const zet = (deel: Partial<AcademyFilters>) => onChange({ ...filters, ...deel });

  const bevestigPostcode = () => {
    const pc = normaliseerPostcode4(pcInvoer);
    if (!pc) {
      setPcFout(true);
      return;
    }
    setPcFout(false);
    bewaarPostcode(pc);
    onPostcode(pc);
    if (filters.km === null) zet({ km: 25 });
  };

  const dagLabel = (d: number) => t(`academy.filter.dag${d}`, { defaultValue: ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'][d - 1] });

  return (
    <div className="space-y-5 rounded-2xl border border-gray-200 bg-white p-4" role="group" aria-label={t('academy.filter.titel', { defaultValue: 'Filters' })}>
      {zoekveld && <FilterGroep titel={t('academy.filter.zoekKop', { defaultValue: 'Zoeken' })}>{zoekveld}</FilterGroep>}

      <FilterGroep titel={t('academy.sorteer.label', { defaultValue: 'Sorteren' })}>
        <label className="flex items-center gap-2">
          <ArrowUpDown className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden="true" />
          <span className="sr-only">{t('academy.sorteer.label', { defaultValue: 'Sorteren' })}</span>
          <select value={filters.sort} onChange={(e) => zet({ sort: e.target.value as AcademySortering })} className={selectCls}>
            <option value="relevantie">{t('academy.sorteer.relevantie', { defaultValue: 'Relevantie' })}</option>
            <option value="populair">{t('academy.sorteer.populair', { defaultValue: '🔥 Meest populair' })}</option>
            <option value="rating">{t('academy.sorteer.rating', { defaultValue: '⭐ Hoogste beoordeling' })}</option>
            <option value="snelst">{t('academy.sorteer.snelst', { defaultValue: '⏱️ Snelst starten' })}</option>
          </select>
        </label>
      </FilterGroep>

      {children}

      {advies && (
        <FilterGroep titel={t('academy.filter.adviesKop', { defaultValue: 'Persoonlijk advies' })}>
          <button
            type="button"
            onClick={() => zet({ advies: !filters.advies })}
            aria-pressed={filters.advies}
            className={`inline-flex w-full items-center justify-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold transition ${
              filters.advies
                ? 'border-amber-500 bg-gradient-to-r from-amber-300 to-emerald-300 text-gray-900 shadow-md shadow-amber-400/30'
                : 'border-amber-400 bg-amber-50 text-amber-800 shadow-[0_0_14px_-4px_rgba(229,164,37,0.6)] hover:border-amber-500'
            }`}
            title={advies.toelichting ?? undefined}
          >
            <Stethoscope className="h-3.5 w-3.5" aria-hidden="true" />
            {t('academy.filter.adviesTraject', { defaultValue: 'Mijn Advies-Traject' })}
          </button>
        </FilterGroep>
      )}

      <FilterGroep titel={t('academy.filter.leervorm', { defaultValue: 'Leervorm' })}>
        <div className="flex flex-wrap gap-1.5">
          {(['e_learning', 'workshop', 'opleiding'] as AcademyLeervorm[]).map((v) => {
            const Icon = VORM_ICON[v];
            const actief = filters.vorm.includes(v);
            return (
              <button
                key={v}
                type="button"
                onClick={() => zet({ vorm: wissel(filters.vorm, v) })}
                aria-pressed={actief}
                title={t(`academy.filter.leervormUitleg.${v}`, {
                  defaultValue: {
                    e_learning: '100% digitaal, individueel tempo, direct starten',
                    workshop: 'Kort en interactief, 1 of 2 dagdelen (fysiek of live-online)',
                    opleiding: 'Meerdaags traject met vaste lesdata en certificering',
                  }[v],
                })}
                className={knop(actief)}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                {t(`academy.leervorm.${v}`, { defaultValue: { e_learning: 'E-Learning', workshop: 'Workshop', opleiding: 'Opleiding' }[v] })}
              </button>
            );
          })}
        </div>
      </FilterGroep>

      <FilterGroep titel={t('academy.filter.shiftKop', { defaultValue: 'Tijdstip (ploegendienst)' })}>
        <div className="flex flex-wrap gap-1.5">
          {(['dag', 'avond', 'nacht'] as AcademyShift[]).map((sh) => {
            const Icon = SHIFT_ICON[sh];
            return (
              <button
                key={sh}
                type="button"
                onClick={() => zet({ shift: wissel(filters.shift, sh) })}
                aria-pressed={filters.shift.includes(sh)}
                title={t(`academy.filter.shiftUitleg.${sh}`, {
                  defaultValue: { dag: 'Sessies tussen 09:00 en 17:00 uur', avond: 'Sessies tussen 18:00 en 22:00 uur — ideaal voor ploegendiensten', nacht: 'Volledig flexibel in te delen, 24/7 beschikbaar' }[sh],
                })}
                className={knop(filters.shift.includes(sh))}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                {t(`academy.filter.shift.${sh}`, { defaultValue: { dag: 'Dag', avond: 'Avond', nacht: 'Nacht / On-Demand' }[sh] })}
              </button>
            );
          })}
        </div>
      </FilterGroep>

      <FilterGroep titel={t('academy.filter.dagen', { defaultValue: 'Lesdagen' })}>
        <div className="grid grid-cols-7 gap-1">
          {DAGEN.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => zet({ dagen: wissel(filters.dagen, d).sort((a, b) => a - b) })}
              aria-pressed={filters.dagen.includes(d)}
              className={`h-8 rounded-lg border text-xs font-semibold transition ${
                filters.dagen.includes(d) ? 'border-[#0B1F3A] bg-[#0B1F3A] text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              }`}
            >
              {dagLabel(d)}
            </button>
          ))}
        </div>
      </FilterGroep>

      <FilterGroep titel={t('academy.filter.budgetKop', { defaultValue: 'Budget & fiscaal' })}>
        <div className="flex flex-col items-start gap-1.5">
          <button
            type="button"
            onClick={() => zet({ budget: !filters.budget })}
            aria-pressed={filters.budget}
            disabled={!wallet?.actief}
            title={!wallet?.actief ? t('academy.filter.budgetInactief', { defaultValue: 'Je werkgever heeft de Academy nog niet geactiveerd.' }) : undefined}
            className={knop(filters.budget, 'whitespace-normal text-left')}
          >
            <WalletIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {t('academy.filter.budget', { defaultValue: 'Alleen passend binnen mijn budget' })}
          </button>
          <button
            type="button"
            onClick={() => !wkrVast && zet({ wkr: !filters.wkr })}
            aria-pressed={filters.wkr || wkrVast}
            aria-disabled={wkrVast}
            title={
              wkrVast
                ? t('academy.filter.wkrVast', { defaultValue: 'De WKR-vrije ruimte van je werkgever is op: hobby-cursussen zijn dit jaar niet beschikbaar.' })
                : t('academy.filter.wkrUitleg', { defaultValue: "Verbergt Rij 4 (Persoonlijke Passie & Hobby's); toont alleen 100% belastingvrije, werkgerelateerde opleidingen." })
            }
            className={knop(filters.wkr || wkrVast, `whitespace-normal text-left ${wkrVast ? 'cursor-not-allowed opacity-60' : ''}`)}
          >
            {wkrVast ? <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> : <Palette className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
            {t('academy.filter.wkr', { defaultValue: "Verberg WKR-risico (Hobby's)" })}
          </button>
        </div>
      </FilterGroep>

      <FilterGroep titel={t('academy.filter.score', { defaultValue: 'Minimale score' })}>
        <label className="flex items-center gap-2">
          <Star className="h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden="true" />
          <span className="sr-only">{t('academy.filter.score', { defaultValue: 'Minimale score' })}</span>
          <select value={filters.score ?? ''} onChange={(e) => zet({ score: (e.target.value || null) as ScoreDrempel | null })} className={selectCls}>
            <option value="">{t('academy.filter.scoreAlle', { defaultValue: 'Alle scores' })}</option>
            {SCORES.map((s) => (
              <option key={s} value={s}>
                {s === '4.5'
                  ? t('academy.filter.scoreUitmuntend', { defaultValue: 'Uitmuntend (⭐ 4.5+)' })
                  : t('academy.filter.scoreTop', { defaultValue: 'Topbeoordeeld (⭐ 4.8+)' })}
              </option>
            ))}
          </select>
        </label>
      </FilterGroep>

      {/* Reistijd-slider (Postcode-Grendel) — alleen bij fysieke leervormen */}
      {reistijd && (
        <FilterGroep titel={t('academy.filter.reistijd', { defaultValue: 'Reistijd' })}>
          {postcode ? (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <MapPin className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden="true" />
                <input
                  type="range"
                  min={KM_MIN}
                  max={KM_MAX + KM_STAP}
                  step={KM_STAP}
                  value={filters.km ?? KM_MAX + KM_STAP}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    zet({ km: v > KM_MAX ? null : v });
                  }}
                  aria-label={t('academy.filter.maxAfstand', { defaultValue: 'Maximale afstand' })}
                  className="h-1.5 min-w-0 flex-1 accent-[#0B1F3A]"
                />
              </div>
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="font-semibold text-gray-900">
                  {filters.km === null
                    ? t('academy.filter.heelNederland', { defaultValue: 'Heel Nederland' })
                    : t('academy.filter.maxKm', { defaultValue: 'Max. {{km}} km', km: filters.km })}
                </span>
                <span className="inline-flex items-center gap-1 text-gray-500">
                  {t('academy.filter.vanafPostcode', { defaultValue: 'vanaf {{pc}}', pc: postcode })}
                  <button
                    type="button"
                    onClick={() => {
                      bewaarPostcode(null);
                      onPostcode(null);
                      zet({ km: null });
                    }}
                    className="rounded p-0.5 text-gray-400 hover:text-gray-900"
                    aria-label={t('academy.filter.postcodeWissen', { defaultValue: 'Postcode wissen' })}
                  >
                    <X className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </span>
              </div>
            </div>
          ) : (
            <form
              className="space-y-2"
              onSubmit={(e) => {
                e.preventDefault();
                bevestigPostcode();
              }}
            >
              <input
                type="text"
                inputMode="numeric"
                maxLength={4}
                value={pcInvoer}
                onChange={(e) => setPcInvoer(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder={t('academy.filter.postcodePlaceholder', { defaultValue: 'Postcode (4 cijfers)' })}
                aria-label={t('academy.filter.postcodePlaceholder', { defaultValue: 'Postcode (4 cijfers)' })}
                aria-invalid={pcFout}
                className={`w-full rounded-xl border bg-white px-3 py-2 text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none ${pcFout ? 'border-red-500' : 'border-gray-300 focus:border-[#0B1F3A]'}`}
              />
              <button type="submit" className={knop(true, 'w-full justify-center')}>
                {t('academy.filter.postcodeOpslaan', { defaultValue: 'Afstand berekenen' })}
              </button>
              <p className="flex items-start gap-1 text-[11px] text-gray-500">
                <Lock className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
                {t('academy.filter.postcodePrivacy', { defaultValue: 'Blijft alleen in je browser; wordt nooit naar de server gestuurd.' })}
              </p>
            </form>
          )}
        </FilterGroep>
      )}
    </div>
  );
};

export default AcademyFilterBalk;
